import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest, popupRequest } from './authConfig';
import { previewHeaders } from './customerPreview';
import { getDemoToken, isDemoMode } from './demoSession';
import { getCustomerAccessToken, hasCustomerSession } from './portal/customerSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Every call to our own API goes through here so the bearer token is attached in exactly one
// place. Pages should use this instead of calling fetch() against API_BASE_URL directly.
//
// Power Automate calls (un-migrated pages) deliberately do NOT go through this — they use their
// own signed URLs and would reject an Authorization header they don't expect.
//
// Two kinds of session can be live, never both in one tab: an internal Entra session (MSAL) or a
// customer session against our own credentials (ADR 0035). The customer branch is checked first
// and is a plain storage read, so an internal session pays almost nothing for it. Every page and
// component below this line is unaware of the difference, which is the point — the customer shell
// reuses the same pages.
// Exported for PTB (apps/ptb) — Solution Testing embeds it via iframe on a completely different
// origin, so it can't share MSAL's cache/cookies the way an in-app fetch can. SolutionTesting.js
// delivers it via postMessage, not a URL param (revised 2026-09-11 after review flagged a bearer
// token in a URL as a real, avoidable exposure — server logs, browser history, Referer). Reuses
// the same apps/api-scoped token used everywhere else in this file rather than a separate scope —
// see apps/ptb/api/src/lib/auth.ts for why, and how cheap that is to split later if ever wanted.
export async function getPtbToken() {
  return getAccessToken();
}

async function getAccessToken() {
  if (isDemoMode) {
    const token = getDemoToken();
    if (!token) throw new Error('Not signed in');
    return token;
  }
  if (hasCustomerSession()) return getCustomerAccessToken();

  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error('Not signed in');

  // Silent acquisition uses the cached token / refresh token. It only prompts if Entra genuinely
  // needs interaction (expired session, changed conditional-access policy) — falling back to a
  // popup rather than failing the request outright.
  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return result.accessToken;
  } catch (err) {
    // Only an interaction_required failure earns a popup (2026-08-12). Anything else — a network
    // blip, a misconfigured scope — used to throw a sign-in window at someone who was perfectly
    // well signed in, and each abandoned popup left MSAL's interaction flag stuck.
    if (!(err instanceof InteractionRequiredAuthError)) throw err;
    const result = await msalInstance.acquireTokenPopup({ ...popupRequest, account });
    return result.accessToken;
  }
}

/**
 * Authenticated fetch that returns the raw `Response`, body unread.
 *
 * Use this only when you need the response as a stream. `apiFetch` below reads and parses the body
 * for you, which is right for ordinary JSON endpoints and actively wrong for a streamed one:
 * calling `.json()` on an NDJSON body throws "Unexpected non-whitespace character after JSON" as
 * soon as it reaches the second line. (Learned the hard way — that was exactly the SOW extraction
 * failure on 2026-07-27.)
 *
 * The caller owns error handling, since a streaming endpoint may report failure in-band after a
 * 200 has already been sent.
 */
export async function apiFetchRaw(path, options = {}) {
  const token = await getAccessToken();
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      // After options.headers, not before: a caller must not be able to opt one request out of
      // the customer view while the rest of the session is in it.
      ...previewHeaders(),
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Authenticated fetch that returns the **parsed body**, not a `Response`.
 *
 * Note the shape: `const projects = await apiFetch('/projects')` — there is no `.ok` to check and
 * no `.json()` to call. Non-2xx throws.
 */
export async function apiFetch(path, options = {}) {
  const response = await apiFetchRaw(path, options);

  if (!response.ok) {
    // Surface the API's own message where there is one. Validation failures say things a user can
    // act on ("Only PDF files can be extracted") and a bare status code throws that away.
    // class-validator returns `message` as an array when several rules fail.
    let detail = '';
    try {
      const body = await response.json();
      const message = body?.message;
      detail = Array.isArray(message) ? message.join('; ') : message || '';
    } catch {
      // no JSON body — fall back to the status code alone
    }
    throw new Error(
      detail || `API ${options.method ?? 'GET'} ${path} failed — ${response.status}`,
    );
  }

  // 204 No Content has no body to parse. A void-returning DELETE/PATCH with no explicit @HttpCode
  // gets Nest's default 200 instead, but still sends no body -- .json() on that throws "Unexpected
  // end of JSON input" (found live testing My Time's unpin flow, 2026-08-05). Reading as text first
  // makes any empty body `null` regardless of status code, instead of assuming only 204 can be one.
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}
